import type { Capability, EscalationRule, ReceptionistData, TransferRule, Verifier } from './types';

/**
 * One store, shown per channel (§9.4). Counts on each page are derived from this
 * array: call 14 of 14, sms 9 of 14, email 8 of 14.
 */
export const CAPABILITIES: Capability[] = [
  {
    id: 'c1', group: 'scheduling', label: 'Book an appointment — returning patient',
    desc: 'The chart already exists; ELVA needs a reason and a slot.',
    on: { call: true, sms: true, email: false },
  },
  {
    id: 'c2', group: 'scheduling', label: 'Book an appointment — new patient',
    desc: 'Requires a full intake before a slot can be held.',
    on: { call: true, sms: false, email: false },
  },
  {
    id: 'c3', group: 'scheduling', label: 'Reschedule an appointment',
    desc: 'Moves an existing booking and releases the old slot.',
    on: { call: true, sms: true, email: false },
  },
  {
    id: 'c4', group: 'scheduling', label: 'Cancel an appointment',
    desc: 'Removes the booking and frees the time.',
    on: { call: true, sms: true, email: false },
  },
  {
    id: 'c5', group: 'scheduling', label: 'Confirm an appointment',
    desc: 'A reply to a reminder. The lightest action there is.',
    on: { call: true, sms: true, email: true },
  },
  {
    id: 'c6', group: 'scheduling', label: 'Book for the whole family',
    desc: 'Several patients in one conversation.',
    on: { call: true, sms: false, email: false },
  },
  {
    id: 'c7', group: 'patient_info', label: 'Add a new patient',
    desc: 'Creates a patient record.',
    on: { call: true, sms: false, email: false },
  },
  {
    id: 'c8', group: 'patient_info', label: 'Update contact details',
    desc: '',
    on: { call: true, sms: true, email: true },
  },
  {
    id: 'c9', group: 'patient_info', label: 'Gather insurance information',
    desc: 'Carrier, member ID, group.',
    on: { call: true, sms: false, email: true },
  },
  {
    id: 'c10', group: 'answering', label: 'Share provider availability',
    desc: 'What is open, and with whom.',
    on: { call: true, sms: true, email: true },
  },
  {
    id: 'c11', group: 'answering', label: 'Quote a price',
    desc: 'Follows the fee setting on General.',
    on: { call: true, sms: true, email: true },
  },
  {
    id: 'c12', group: 'answering', label: 'Share the in-network insurance list',
    desc: 'The carriers this practice takes.',
    on: { call: true, sms: false, email: true },
  },
  {
    id: 'c13', group: 'answering', label: 'Work out why the patient is coming in',
    desc: 'Works out which visit the patient needs.',
    on: { call: true, sms: true, email: true },
  },
  {
    id: 'c14', group: 'answering', label: 'Answer a practice question',
    desc: 'From an entry in the Knowledge base, and from nowhere else.',
    on: { call: true, sms: true, email: true },
  },
];

/** A matching sender counts as verification — it proves the contact point, not the person. */
export const VERIFIERS: Verifier[] = [
  {
    id: 'v1',
    label: {
      call: 'The number called from matches the chart',
      sms: 'The number texted from matches the chart',
      email: 'The address written from matches the chart',
    },
    on: { call: true, sms: true, email: true },
  },
  {
    id: 'v2',
    label: { call: 'Date of birth', sms: 'Date of birth', email: 'Date of birth' },
    on: { call: true, sms: false, email: false },
  },
  {
    id: 'v3',
    label: { call: 'ZIP code', sms: 'ZIP code', email: 'ZIP code' },
    on: { call: false, sms: false, email: false },
  },
  {
    id: 'v4',
    label: { call: 'Full name', sms: 'Full name', email: 'Full name' },
    on: { call: false, sms: false, email: true },
  },
];

export const TRANSFER_RULES: TransferRule[] = [
  {
    id: 't1', condition: 'Billing disputes, payment plans, or any financial question',
    destination: '+1 (210) 555–0101', ifNobodyPicksUp: 'take_message',
    sayBefore: 'Let me put you through to Sarah, who handles billing. One moment.',
  },
  {
    id: 't2', condition: 'Treatment plan questions about something already quoted',
    destination: '+1 (210) 555–0102', ifNobodyPicksUp: 'take_message',
  },
  {
    id: 't3', condition: 'The patient asks for a person, or the conversation turns tense',
    destination: '+1 (210) 555–0100', ifNobodyPicksUp: 'take_message',
  },
];

export const SMS_ESCALATIONS: EscalationRule[] = [
  { id: 'e1', condition: 'Complaints about earlier care, or about a member of staff', threadBehaviour: 'keep_replying', urgent: false },
  { id: 'e2', condition: 'Clinical questions that need a provider’s judgement', threadBehaviour: 'keep_replying', urgent: false },
  { id: 'e3', condition: 'Billing disputes and refund requests', threadBehaviour: 'keep_replying', urgent: false },
];

export const EMAIL_ESCALATIONS: EscalationRule[] = [
  { id: 'x1', condition: 'Legal questions, demand letters, anything mentioning litigation', threadBehaviour: 'stop_replying', urgent: false },
  { id: 'x2', condition: 'Complaints about earlier care, or refund requests', threadBehaviour: 'stop_replying', urgent: false },
  { id: 'x3', condition: 'The patient sounds distressed, urgent or upset', threadBehaviour: 'stop_replying', urgent: false },
];

/** AI Receptionist (AgentRecep board). ELVA always picks up. */
export const RECEPTIONIST: ReceptionistData = {
  greetingOpen: 'Thanks for calling Alamo Heights Dental, this is Elva. How can I help you today?',
  greetingClosed:
    'Thanks for calling Alamo Heights Dental. The office is closed right now — we open at 8 tomorrow morning. I can book you in, take a message, or if this is a dental emergency I can get you to the on-call dentist. Which would you like?',
  emergencyText: [
    'Severe pain rated 7 or higher out of 10.',
    'Visible facial swelling.',
    'Trauma to teeth or jaw from an accident.',
    'Uncontrolled bleeding more than 12 hours after an extraction.',
    'Any post-op complication within 72 hours of surgery.',
    'Active dental abscess with fever.',
  ].join('\n'),
  emergencyLastRead: '26 September, 4:12pm',
  emergencyRules: [
    {
      id: 'er1', quote: 'Severe pain, 7 or higher out of ten', state: 'in_force', enabled: true,
      effect: 'ELVA asks for a rating and, at 7 or above, stops offering the normal diary and goes to the emergency path.',
    },
    {
      id: 'er2', quote: 'Visible facial swelling', state: 'in_force', enabled: true,
      effect: 'Same-day. ELVA asks the patient to describe it, records what they say and flags it for a person — it does not assess it.',
    },
    {
      id: 'er3', quote: 'Trauma to teeth or jaw from an accident', state: 'in_force', enabled: true,
      effect: 'Same-day, and ELVA skips the usual intake questions.',
    },
    {
      id: 'er4', quote: 'Uncontrolled bleeding more than 12 hours after an extraction', state: 'in_force', enabled: true,
      effect: 'Post-op alert. ELVA collects the details and follows your after-hours setting below; in opening hours it hands the call to a person.',
    },
    {
      id: 'er5', quote: 'Any post-op complication within 72 hours of surgery', state: 'in_force', enabled: true,
      effect: '',
    },
    {
      id: 'er6', quote: 'Active dental abscess with fever', state: 'in_force', enabled: true,
      effect: 'ELVA treats an abscess on its own as an emergency too, without waiting to hear about a fever.',
      note: 'Your sentence names two signals; ELVA acts on either one. Edit the sentence if you want both required.',
    },
  ],
  houseRulesText: [
    'Never give medical advice. Always offer a clinician callback for clinical questions.',
    'When a patient seems anxious, slow down and offer reassurance.',
    'Never quote a specific dollar amount unless we have verified insurance.',
    'Anyone in pain gets a same-day slot, always.',
    'Never double-book Dr. Saito. She has said no.',
    'Do not accept patients who have not paid an outstanding balance.',
  ].join('\n'),
  houseRulesLastRead: '26 September, 4:12pm',
  houseRules: [
    {
      id: 'hr1', quote: 'Never give medical advice. Always offer a clinician callback for clinical questions.',
      state: 'in_force', enabled: true,
      effect: 'Already true for every agent. It can’t be switched off.',
    },
    {
      id: 'hr2', quote: 'When a patient seems anxious, slow down and offer reassurance.',
      state: 'in_force', enabled: true,
      effect: 'ELVA slows down, offers one choice at a time, and checks the patient is following.',
    },
    {
      id: 'hr3', quote: 'Never quote a specific dollar amount unless we have verified insurance.',
      state: 'in_force', enabled: true,
      effect: 'Already set, and this sentence matches it. ELVA gives a range and offers to check the patient’s own cover.',
      note: 'Set on General › Money.',
    },
    {
      id: 'hr4', quote: 'Anyone in pain gets a same-day slot, always.',
      state: 'in_force', enabled: true,
      effect: 'ELVA stops offering the ordinary diary and works the emergency path in card 03 — including double-booking when that is the only way to fit them in.',
    },
    {
      id: 'hr5', quote: 'Never double-book Dr. Saito. She has said no.',
      state: 'conflict', enabled: false,
      effect: 'Not in force. This and “anyone in pain gets a same-day slot, always” cannot both hold. On a day when Dr. Saito’s column is the only same-day space left, ELVA has to either turn away a patient in pain or double-book her.',
      note: 'The older rule wins and this one is off. Rewrite it to say what should happen.',
      resolution: 'Anyone in pain gets a same-day slot, with any provider except Dr. Saito',
    },
    {
      id: 'hr6', quote: 'Do not accept patients who have not paid an outstanding balance.',
      state: 'not_applied', enabled: false,
      effect: 'Not applied. ELVA cannot see a balance at the moment it is booking, so it cannot act on this.',
      note: 'ELVA can’t see balances when it books. The front desk raises the balance at the visit.',
    },
  ],
  doubleBookEmergency: true,
  heldSlots: '2',
  heldUntil: '10:00',
  skipInsuranceVerification: true,
  afterHoursEmergency: 'page',
  onCallNumber: '(210) 555-0190',
  onCallWho: 'Dr. Carter',
};
